import { useState, useEffect, useMemo } from "react";
import { Plus, X as XIcon, AlertCircle } from "lucide-react";
import Modal from "../ui/Modal";
import Input from "../ui/Input";
import Select from "../ui/Select";
import Button from "../ui/Button";
import { toISO, today, addMonths } from "../../lib/dateUtils";
import { formatUSD } from "../../lib/formatters";

const uid = () => `tmp_${Math.random().toString(36).slice(2, 9)}`;

const blankInvoice = (date) => ({ key: uid(), amount: "", dueDate: date || toISO(today()) });

// Create a new deal for a client.
// A deal = one or more payment rows sharing the same splitGroupId.
// The split sum is enforced to exactly match the deal total before save.
export default function DealForm({ open, onClose, onSubmit, client, offers }) {
  const [name, setName] = useState("");
  const [offerId, setOfferId] = useState(client?.primaryOfferId || "");
  const [total, setTotal] = useState("");
  const [splitOn, setSplitOn] = useState(false);
  const [invoices, setInvoices] = useState([blankInvoice()]);
  const [errors, setErrors] = useState({});

  // Reset state every time the modal opens for a fresh deal entry.
  useEffect(() => {
    if (open) {
      setName("");
      setOfferId(client?.primaryOfferId || "");
      setTotal("");
      setSplitOn(false);
      setInvoices([blankInvoice()]);
      setErrors({});
    }
  }, [open, client?.primaryOfferId]);

  const totalNum = Number(total) || 0;
  const subtotal = useMemo(
    () => invoices.reduce((s, r) => s + (Number(r.amount) || 0), 0),
    [invoices]
  );
  const diff = subtotal - totalNum;
  const matches = Math.abs(diff) < 0.005 && totalNum > 0;

  const addInvoice = () => {
    // Default the next due date to one month after the previous row's date.
    const last = invoices[invoices.length - 1];
    const baseDate = last?.dueDate ? new Date(last.dueDate) : today();
    const next = toISO(addMonths(baseDate, 1));
    setInvoices((rows) => [...rows, blankInvoice(next)]);
  };

  const removeInvoice = (key) => {
    setInvoices((rows) => (rows.length > 1 ? rows.filter((r) => r.key !== key) : rows));
  };

  const updateInvoice = (key, patch) => {
    setInvoices((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  // When the user toggles split OFF, collapse to a single invoice with amount = total.
  const handleToggleSplit = (on) => {
    setSplitOn(on);
    if (!on) {
      setInvoices([{ key: uid(), amount: total, dueDate: invoices[0]?.dueDate || toISO(today()) }]);
    } else if (invoices.length === 1) {
      // Pre-fill 2 rows when opening split mode so user sees the structure.
      setInvoices([
        { ...invoices[0], amount: "" },
        blankInvoice(toISO(addMonths(today(), 1))),
      ]);
    }
  };

  // Keep single-invoice amount in sync with total when split is OFF.
  useEffect(() => {
    if (!splitOn && invoices.length === 1) {
      setInvoices((rows) => [{ ...rows[0], amount: total }]);
    }
  }, [total, splitOn]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!offerId) errs.offerId = "Pick an offer";
    if (!(totalNum > 0)) errs.total = "Deal total must be > 0";
    invoices.forEach((r, i) => {
      if (!(Number(r.amount) > 0)) errs[`amt_${r.key}`] = "Required";
      if (!r.dueDate) errs[`date_${r.key}`] = "Required";
    });
    if (splitOn && !matches) errs.split = "Subtotal must exactly match deal total";
    if (!splitOn && Math.abs((Number(invoices[0]?.amount) || 0) - totalNum) > 0.005) {
      errs.split = "Invoice amount must equal deal total";
    }
    if (Object.keys(errs).length) { setErrors(errs); return; }

    // Build the payment rows. status="scheduled" for all (user can mark paid later).
    const rows = invoices.map((r, i) => ({
      clientId: client.id,
      offerId: offerId || null,
      amount: Number(r.amount) || 0,
      amountRevenue: Number(r.amount) || 0,
      bookedDate: r.dueDate,
      dueDate: r.dueDate,
      paidDate: null,
      status: "scheduled",
      note: name
        ? (invoices.length > 1 ? `${name} — Invoice ${i + 1} of ${invoices.length}` : name)
        : (invoices.length > 1 ? `Invoice ${i + 1} of ${invoices.length}` : ""),
    }));
    onSubmit(rows);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title={`New deal — ${client?.name || "Client"}`}>
      <form onSubmit={handleSubmit} className="space-y-3">
        <Input
          label="Deal name (optional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. SEO Backlinks Q2"
        />
        <Select label="Offer *" value={offerId} onChange={(e) => setOfferId(e.target.value)} error={errors.offerId}>
          <option value="">Select offer</option>
          {offers.map((o) => <option key={o.id} value={o.id}>{o.name}{o.department ? ` (${o.department})` : ""}</option>)}
        </Select>
        <Input
          label="Deal total (USD) *"
          type="number"
          step="0.01"
          value={total}
          onChange={(e) => setTotal(e.target.value)}
          error={errors.total}
          placeholder="3000.00"
        />

        <label className="flex items-start gap-2.5 cursor-pointer rounded-lg border border-gray-200 dark:border-[var(--border-soft)] p-3">
          <input
            type="checkbox"
            checked={splitOn}
            onChange={(e) => handleToggleSplit(e.target.checked)}
            className="mt-0.5 cursor-pointer"
          />
          <span>
            <span className="block text-sm font-medium text-brand-ink dark:text-[var(--text-primary)]">Split into multiple invoices</span>
            <span className="block text-[11px] text-brand-slate">
              Subtotal of all invoices must exactly match the deal total.
            </span>
          </span>
        </label>

        <div className="space-y-2">
          <div className="text-xs font-medium text-brand-slate uppercase tracking-wide">
            {splitOn ? "Invoices" : "Invoice"}
          </div>
          {invoices.map((r, i) => (
            <div key={r.key} className="grid grid-cols-[1fr_140px_auto] gap-2 items-end">
              <Input
                label={`#${i + 1} amount`}
                type="number"
                step="0.01"
                value={r.amount}
                disabled={!splitOn}
                onChange={(e) => updateInvoice(r.key, { amount: e.target.value })}
                error={errors[`amt_${r.key}`]}
              />
              <Input
                label="Due date"
                type="date"
                value={r.dueDate}
                onChange={(e) => updateInvoice(r.key, { dueDate: e.target.value })}
                error={errors[`date_${r.key}`]}
              />
              {splitOn && invoices.length > 1 ? (
                <button
                  type="button"
                  onClick={() => removeInvoice(r.key)}
                  className="mb-[1px] px-2 py-2 rounded-lg border border-gray-200 dark:border-[var(--border-soft)] text-brand-slate hover:text-red-600 hover:border-red-200"
                  title="Remove this invoice"
                >
                  <XIcon size={14} />
                </button>
              ) : (
                <div className="w-9" />
              )}
            </div>
          ))}
          {splitOn && (
            <button
              type="button"
              onClick={addInvoice}
              className="text-xs text-brand-blue hover:underline inline-flex items-center gap-1"
            >
              <Plus size={12} /> Add another invoice
            </button>
          )}
        </div>

        {/* Subtotal vs deal total reconciliation. Always visible so the user
            sees the validation in real time. Red when mismatch, green when match. */}
        <div className={`rounded-lg border p-3 ${matches ? "bg-emerald-50 border-emerald-200 dark:bg-emerald-900/10" : "bg-red-50 border-red-200 dark:bg-red-900/10"}`}>
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">Subtotal</span>
            <span className={`font-bold ${matches ? "text-emerald-700 dark:text-emerald-300" : "text-red-700 dark:text-red-300"}`}>
              {formatUSD(subtotal)}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm mt-1">
            <span className="font-medium">Deal total</span>
            <span className="font-bold text-brand-ink dark:text-[var(--text-primary)]">{formatUSD(totalNum)}</span>
          </div>
          {!matches && totalNum > 0 && (
            <div className="flex items-start gap-1.5 mt-2 text-xs text-red-700 dark:text-red-300">
              <AlertCircle size={12} className="mt-0.5 shrink-0" />
              <span>
                {diff > 0
                  ? `Subtotal is ${formatUSD(diff)} OVER the deal total`
                  : `Subtotal is ${formatUSD(Math.abs(diff))} UNDER the deal total`}
                . Fix the amounts so they add up exactly.
              </span>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={!matches}>
            {splitOn ? `Create deal (${invoices.length} invoices)` : "Create deal"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
